import { Grid, Button, Divider } from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { ReactComponent as ResetIcon } from 'assets/images/reset-icon.svg'
import { STORE_COLORS } from 'styles/storeColors'

import { ColorPicker } from '../ColorPicker'
import { ImageUploader } from '../ImageUploader'
import { useStyles } from '../styles'
import { Style } from '../types'

interface StoreProps {
  style: Style
  setStyle: (style: Style) => void
  setLogoFile: (file: File | undefined) => void
  onSave: () => void
}

const Store: React.FC<StoreProps> = ({ style, setStyle, setLogoFile, onSave }) => {
  const classes = useStyles()
  const [isResetting, setIsResetting] = useState(false)

  const [primaryColor, setPrimaryColor] = useState<string>(style.storeColors.PRIMARY_MAIN)
  const [primaryContrastColor, setPrimaryContrastColor] = useState<string>(
    style.storeColors.PRIMARY_CONTRAST,
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [secondaryColor, setSecondaryColor] = useState<string>(style.storeColors.SECONDARY_MAIN)
  const [backgroundColor, setBackgroundColor] = useState<string>(style.storeColors.BACKGROUND)
  const [storeLogoPreview, setStoreLogoPreview] = useState<string>(style.storeLogoUrl)

  useEffect(() => {
    if (isResetting) {
      onSave()
      setIsResetting(false)
    }
  }, [style, isResetting, onSave])

  useEffect(() => {
    setPrimaryColor(style.storeColors.PRIMARY_MAIN)
    setPrimaryContrastColor(style.storeColors.PRIMARY_CONTRAST)
    setSecondaryColor(style.storeColors.SECONDARY_MAIN)
    setBackgroundColor(style.storeColors.BACKGROUND)
    setStoreLogoPreview(style.storeLogoUrl)
  }, [style])

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color)
    setStyle({
      ...style,
      storeColors: {
        ...style.storeColors,
        PRIMARY_MAIN: color,
      },
    })
  }

  const handlePrimaryContrastColorChange = (color: string) => {
    setPrimaryContrastColor(color)
    setStyle({
      ...style,
      storeColors: {
        ...style.storeColors,
        PRIMARY_CONTRAST: color,
      },
    })
  }

  const handleBackgroundChange = (color: string) => {
    setBackgroundColor(color)
    setStyle({
      ...style,
      storeColors: {
        ...style.storeColors,
        BACKGROUND: color,
      },
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color)
    setStyle({
      ...style,
      storeColors: {
        ...style.storeColors,
        SECONDARY_MAIN: color,
      },
    })
  }

  const handleLogoChange = (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setStoreLogoPreview(previewUrl)
    setLogoFile(file)
    setStyle({
      ...style,
      storeLogoUrl: previewUrl,
    })
  }

  const handleLogoDelete = () => {
    setStoreLogoPreview('')
    setLogoFile(undefined)
    setStyle({
      ...style,
      storeLogoUrl: '',
    })
  }

  const reset = () => {
    // Primero actualizamos los estados locales
    setPrimaryColor(STORE_COLORS.PRIMARY_MAIN)
    setPrimaryContrastColor(STORE_COLORS.PRIMARY_CONTRAST)
    setSecondaryColor(STORE_COLORS.SECONDARY_MAIN)
    setBackgroundColor(STORE_COLORS.BACKGROUND)
    setStoreLogoPreview('')
    setLogoFile(undefined)

    // Luego actualizamos el estado del padre
    const newStyle = {
      ...style,
      storeColors: {
        PRIMARY_MAIN: STORE_COLORS.PRIMARY_MAIN,
        PRIMARY_CONTRAST: STORE_COLORS.PRIMARY_CONTRAST,
        SECONDARY_MAIN: STORE_COLORS.SECONDARY_MAIN,
        BACKGROUND: STORE_COLORS.BACKGROUND,
      },
      storeLogoUrl: '',
    }

    setStyle(newStyle)
    setIsResetting(true)
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Button
          onClick={reset}
          variant="outlined"
          color="primary"
          className={classes.resetButton}
          startIcon={<ResetIcon className={classes.resetIcon} />}
        >
          Restaurar valores por defecto
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Colores</div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Banner y botones"
          color={primaryColor}
          handler={handlePrimaryColorChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Fuentes"
          color={primaryContrastColor}
          handler={handlePrimaryContrastColorChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker label="Fondos" color={backgroundColor} handler={handleBackgroundChange} />
      </Grid>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Logo</div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <ImageUploader
          label="Logo"
          preview={storeLogoPreview}
          onChange={handleLogoChange}
          onDelete={handleLogoDelete}
        />
      </Grid>
    </Grid>
  )
}

export { Store }
